import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2 } from 'lucide-react';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { EmptyState } from '../../../components/EmptyState';
import { Tabs, TabsList, TabsTrigger } from '../../../components/Tabs';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import { useAttendanceStore, type Adjustment } from '../../../store/attendanceStore';
import { AdjustmentComparison } from './AdjustmentsPanel';
import { DayDetailSheet } from './DayDetailSheet';
import { AdjustmentStatusBadge, adjustmentLabel, fmtDate, fmtDateTime } from './attendanceUi';

/**
 * Review queue. The server decides who may review what: a manager only their
 * direct reports, HR everyone, and nobody their own request.
 */
export function ApprovalsPanel() {
    const { adjustments, adjustmentsLoading, fetchAdjustments, reviewAdjustment } = useAttendanceStore();
    const [view, setView] = useState<'pending' | 'reviewed'>('pending');
    const [review, setReview] = useState<{ a: Adjustment; decision: 'approved' | 'rejected' } | null>(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [detail, setDetail] = useState<{ employeeId: string; date: string } | null>(null);

    const load = () => fetchAdjustments().catch((e) => toast.error(e.message));
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const list = adjustments.filter((a) => (view === 'pending' ? a.status === 'pending' : a.status !== 'pending'));

    const submit = async () => {
        if (!review) return;
        if (review.decision === 'rejected' && !note.trim()) { toast.error('Tell the employee why it was rejected.'); return; }
        setBusy(true);
        try {
            await reviewAdjustment(review.a.id, review.decision, note.trim() || undefined);
            toast.success(review.decision === 'approved' ? 'Approved — attendance updated' : 'Rejected');
            setReview(null); setNote('');
            load();
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <h2 className="font-display text-card-title text-foreground">Adjustment requests</h2>
                <Tabs value={view} onValueChange={(v) => setView(v as 'pending' | 'reviewed')}>
                    <TabsList>
                        <TabsTrigger value="pending">Waiting ({adjustments.filter((a) => a.status === 'pending').length})</TabsTrigger>
                        <TabsTrigger value="reviewed">Reviewed</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>
            {list.length === 0 ? (
                <EmptyState className="m-4 border-0" icon={<CheckCircle2 />} title={adjustmentsLoading ? 'Loading…' : view === 'pending' ? 'Nothing to review' : 'No reviewed requests yet'}
                    description={view === 'pending' ? 'New requests from your team appear here.' : ''} />
            ) : (
                <ul className="divide-y divide-border">
                    {list.map((a) => (
                        <li key={a.id} className="space-y-2 px-4 py-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <p className="text-sm font-medium text-foreground">
                                        {a.employeeName || 'Employee'}{a.employeeCode ? <span className="font-normal text-muted-foreground"> · {a.employeeCode}</span> : null}
                                    </p>
                                    <p className="text-xs text-muted-foreground">{adjustmentLabel(a.type)} · {fmtDate(a.date)} · sent {fmtDateTime(a.createdAt)}</p>
                                </div>
                                <AdjustmentStatusBadge status={a.status} />
                            </div>
                            <AdjustmentComparison a={a} />
                            <p className="text-xs text-muted-foreground"><span className="text-foreground">Reason:</span> {a.reason}</p>
                            {a.status !== 'pending' && a.reviewerName && (
                                <p className="text-xs text-muted-foreground">By {a.reviewerName}{a.reviewedAt ? `, ${fmtDateTime(a.reviewedAt)}` : ''}{a.reviewerComment ? ` — “${a.reviewerComment}”` : ''}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                                {a.status === 'pending' && (a.isMine
                                    ? <span className="text-xs text-muted-foreground">Your own request — another manager or HR must review it.</span>
                                    : <>
                                        <Button size="sm" variant="secondary" onClick={() => { setNote(''); setReview({ a, decision: 'approved' }); }}>Approve</Button>
                                        <Button size="sm" variant="ghost" onClick={() => { setNote(''); setReview({ a, decision: 'rejected' }); }}>Reject</Button>
                                    </>)}
                                <Button size="sm" variant="ghost" onClick={() => setDetail({ employeeId: a.employeeId, date: a.date })}>View day</Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{review?.decision === 'approved' ? 'Approve this adjustment?' : 'Reject this adjustment?'}</DialogTitle>
                        <DialogDescription>
                            {review?.decision === 'approved'
                                ? 'The attendance result is updated from the requested times. The original record is kept for audit.'
                                : 'Nothing changes. The employee sees your reason.'}
                        </DialogDescription>
                    </DialogHeader>
                    {review && <div className="rounded-lg border border-border p-3"><AdjustmentComparison a={review.a} /></div>}
                    <textarea className="min-h-20 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30"
                        placeholder={review?.decision === 'rejected' ? 'Reason for rejecting (required)' : 'Comment (optional)'}
                        value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setReview(null)} disabled={busy}>Cancel</Button>
                        <Button variant={review?.decision === 'rejected' ? 'destructive' : 'default'} onClick={submit} isLoading={busy}>
                            {review?.decision === 'approved' ? 'Approve' : 'Reject'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <DayDetailSheet target={detail} onClose={() => setDetail(null)} />
        </Card>
    );
}
